import React, { memo, useState, useEffect } from 'react';
import { connect, useDispatch } from 'react-redux';
import { createStructuredSelector } from 'reselect';
import { compose } from 'redux';
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/core/styles';
import { remove } from 'lodash';
import { useTranslation } from '../../../src/i18n';
import DropzoneInput from '../../../components/DropzoneInput';
import EditModeCard, { EditModeCardSection } from '../../../components/EditModeCard';
import {
  MEDIA_TYPES_IDS,
  PRIVACY_ATTRIBUTE_IDS,
  MEDIA_TAGS_TYPES_IDS,
  TP,
  MEDIA_ATTRIBUTES_TYPES_IDS,
  TAGS_AVAILABILITY_TYPES,
  urlRegex,
} from '../../../constants/consts';
import endpointConstants, { MEDIA_ATTRIBUTES } from '../../../constants/endpointConstants';
import {
  selectMediaAttributesOptions,
  selectLoadingFieldName,
  selectProductionImages,
  selectImageFormatsNames,
} from '../selectors';
import EditMediaCard from '../../../components/EditComponents/EditMediaCard';
import {
  updateMediaAction,
  clientRemoveMediaAction,
  uploadSeveralMediaAction,
  getProductionImagesAction,
  addProductionImageAction,
  getProductionImageFormatsAction,
} from '../actions';
import Button from '../../../components/Button';
import AddMediaDetailsModal from '../../../components/EditComponents/AddMediaDetailsModal';
import { updateOne, getWholeList, useBufferValueAction, createEntity } from '../../App/actions';
import FbCircularProgress from '../../../components/FbCircularProgress';
import { selectBufferValueData, selectPrivacyAttributes, selectPrivacyOptions } from '../../App/selectors';
import ActionStatusModal from '../../../components/ActionStatusModal';
import TextButton from '../../../components/TextButton';
import AddFieldWithButton from '../../../components/EditComponents/AddFieldWithButton';
import useImageValidate from '../../../utils/hooks/useImageValidate';
import LoadingModal from '../../../components/LoadingModal';
import { joinMediaFormats } from '../../../utils/editCommon';

const useStyles = makeStyles(() => ({
  uploadDropzone: {
    marginBottom: 10,
  },
}));

const ImagesSection = ({
  production,
  images,
  updatedImage,
  privacyOptions,
  privacyAttributes,
  imageAttributesOptions,
  loadingMediaType,
  imageFormats,
}) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const classes = useStyles();
  const validateImage = useImageValidate();

  const [isEditModalOpen, setEditModalOpen] = useState(false);
  const [modifyMedia, setModifyMedia] = useState(null);
  const [mediaToDelete, setMediaToDelete] = useState(null);

  useEffect(() => {
    dispatch(
      getWholeList({
        endpoint: endpointConstants.MEDIA,
        sagaRoutine: getProductionImagesAction,
        params: {
          queryParams: {
            media_tag_type_id: MEDIA_TAGS_TYPES_IDS.PRODUCTION,
            media_tag_id: production?.id,
            media_type_id: MEDIA_TYPES_IDS.IMAGE,
          },
        },
      }),
    );
    if (!imageFormats?.length) {
      dispatch(
        getWholeList({
          endpoint: MEDIA_ATTRIBUTES,
          sagaRoutine: getProductionImageFormatsAction,
          params: {
            queryParams: {
              attribute_type_id: MEDIA_ATTRIBUTES_TYPES_IDS.IMAGE_FORMAT,
            },
          },
        }),
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const closeEditMediaModal = () => {
    setEditModalOpen(false);
    setModifyMedia(null);
  };

  const uploadImages = async uploadingImages => {
    const imagesToUpload = [];
    // eslint-disable-next-line no-restricted-syntax
    for (const image of uploadingImages) {
      const imageData = {
        file: { data: `@${image?.filename}` },
        mediaType: {
          id: MEDIA_TYPES_IDS.IMAGE,
        },
        privacyAttribute: privacyAttributes.find(p => p.id === PRIVACY_ATTRIBUTE_IDS.PUBLIC) || privacyAttributes[0],
        tags: [
          {
            tagType: { id: MEDIA_TAGS_TYPES_IDS.PRODUCTION },
            tagId: production?.id,
            availabilityTypes: [TAGS_AVAILABILITY_TYPES.GALLERY],
          },
        ],
      };
      imagesToUpload.push({ data: imageData, file: { [image?.filename]: image?.file } });
    }
    dispatch(uploadSeveralMediaAction({ data: imagesToUpload }));
  };

  const uploadImageUrl = url => {
    updateWebLinkUploadError('');
    const imageData = {
      url,
      mediaType: {
        id: MEDIA_TYPES_IDS.IMAGE,
      },
      privacyAttribute: privacyAttributes.find(p => p.id === PRIVACY_ATTRIBUTE_IDS.PUBLIC) || privacyAttributes[0],
      tags: [
        {
          tagType: { id: MEDIA_TAGS_TYPES_IDS.PRODUCTION },
          tagId: production?.id,
          availabilityTypes: [TAGS_AVAILABILITY_TYPES.GALLERY],
        },
      ],
    };
    // eslint-disable-next-line no-await-in-loop
    dispatch(
      createEntity({
        endpoint: endpointConstants.MEDIA,
        sagaRoutine: addProductionImageAction,
        params: imageData,
        fieldName: 'singleImage',
        callback: () => {
          updateWebLinkInputValue('');
        },
      }),
    );
  };

  const updateMedia = updatedMedia => {
    dispatch(
      updateOne({
        endpoint: endpointConstants.MEDIA,
        sagaRoutine: useBufferValueAction,
        id: updatedMedia.id,
        params: { data: updatedMedia },
        callback: entity => dispatch(updateMediaAction(entity, 'image')),
      }),
    );
    closeEditMediaModal();
  };

  const updatePrivacy = (value, updatedMedia) => {
    dispatch(
      updateOne({
        endpoint: endpointConstants.MEDIA,
        sagaRoutine: useBufferValueAction,
        id: updatedMedia.id,
        params: { privacyAttribute: { id: value } },
        callback: entity => dispatch(updateMediaAction(entity, 'image')),
      }),
    );
  };

  const updateMediaDetails = (data, id) => {
    if (id) {
      dispatch(
        updateOne({
          endpoint: endpointConstants.MEDIA,
          sagaRoutine: useBufferValueAction,
          id,
          params: data,
          callback: entity => {
            closeEditMediaModal();
            dispatch(updateMediaAction(entity, 'image'));
          },
        }),
      );
    } else {
      closeEditMediaModal();
    }
  };

  const deleteMedia = () => {
    const mediaTags = [...(mediaToDelete?.tags || [])];
    remove(mediaTags, tag => tag?.tagType?.id === MEDIA_TAGS_TYPES_IDS.PRODUCTION && tag?.tagId === production?.id);
    dispatch(
      updateOne({
        endpoint: endpointConstants.MEDIA,
        sagaRoutine: useBufferValueAction,
        id: mediaToDelete?.id,
        params: {
          tags: mediaTags,
        },
        callback: deletedItem => {
          dispatch(clientRemoveMediaAction(deletedItem?.id, 'image'));
          closeConfirmationModal();
        },
      }),
    );
  };

  const [webLinkInputValue, updateWebLinkInputValue] = React.useState('');
  const [webLinkUploadError, updateWebLinkUploadError] = React.useState(null);

  const closeConfirmationModal = () => {
    setMediaToDelete(null);
  };

  return (
    <>
      <EditModeCard>
        <EditModeCardSection first>
          <div>{t(`${TP}.FN_IMAGES_TITLE`)}</div>
          <div>
            {images?.loading ? (
              <FbCircularProgress />
            ) : (
              images?.data?.map(image => (
                <EditMediaCard
                  media={image}
                  mediaType="image"
                  key={`img-${image.id}`}
                  editClick={() => {
                    setModifyMedia(image);
                    setEditModalOpen(true);
                  }}
                  privacyOptions={privacyOptions}
                  onPrivacyChange={e => updatePrivacy(e.value, image)}
                  onDelete={() => setMediaToDelete(image)}
                />
              ))
            )}
          </div>
          <DropzoneInput
            type="image"
            accept={imageFormats}
            onFileUpload={uploadImages}
            className={classes.uploadDropzone}
            subtitle={t(`${TP}.FN_UPLOAD_FILE_FORMAT_TEMPLATE`, {
              format: joinMediaFormats(imageFormats),
            })}
          />
          <AddFieldWithButton
            value={webLinkInputValue}
            onChange={updateWebLinkInputValue}
            onAdd={url => validateImage(url, uploadImageUrl)}
            isLoading={loadingMediaType === 'singleImage'}
            errorMessage={webLinkUploadError || `${TP}.FN_WEB_LINK_INVALID`}
            validateRegex={urlRegex}
            label={`${TP}.FN_ADD_IMAGE_LINK_TITLE`}
            placeholder={`${TP}.FN_ADD_IMAGE_LINK`}
          />
        </EditModeCardSection>
      </EditModeCard>
      {isEditModalOpen && (
        <AddMediaDetailsModal
          isOpened
          submitFunc={data => {
            if (modifyMedia.preupId) {
              updateMedia({ ...modifyMedia, title: data.tile, tags: [...modifyMedia.tags, ...data.tags] });
            } else {
              updateMediaDetails(data, modifyMedia.id);
            }
          }}
          onClose={() => setEditModalOpen(false)}
          entity={modifyMedia}
          type={modifyMedia?.mediaType?.slug}
          typeOptions={imageAttributesOptions}
          isLoading={updatedImage?.loading}
          fixedProduction
        />
      )}
      {mediaToDelete && (
        <ActionStatusModal
          isOpened
          onClose={closeConfirmationModal}
          type="warning"
          title={t(`${TP}.FN_DELETE_MEDIA_CONFIRMATION`)}
          buttons={[
            <TextButton onClick={deleteMedia}>{t(`${TP}.m_DELETE`)}</TextButton>,
            <Button onClick={closeConfirmationModal}>{t(`${TP}.m_CANCEL`)}</Button>,
          ]}
        >
          {updatedImage?.loading && <FbCircularProgress />}
        </ActionStatusModal>
      )}
      {loadingMediaType === 'images' && <LoadingModal title={t(`${TP}.FN_UPLOADING_IMAGES`)} />}
    </>
  );
};

ImagesSection.propTypes = {
  production: PropTypes.object,
  updatedImage: PropTypes.object,
  imageFormats: PropTypes.arrayOf(PropTypes.string),
};

const mapStateToProps = createStructuredSelector({
  images: selectProductionImages(),
  updatedImage: selectBufferValueData(),
  privacyOptions: selectPrivacyOptions(),
  privacyAttributes: selectPrivacyAttributes(),
  imageAttributesOptions: selectMediaAttributesOptions(MEDIA_ATTRIBUTES_TYPES_IDS.IMAGE),
  loadingMediaType: selectLoadingFieldName(),
  imageFormats: selectImageFormatsNames(),
});

const withConnect = connect(mapStateToProps, null);

export default compose(memo, withConnect)(ImagesSection);
